/**
 * Maintenance Windows Tab Component
 *
 * Displays and manages scheduled maintenance windows for clients.
 * Supports weekly recurring and one-time maintenance windows.
 *
 * @module pages/Settings/SLAConfiguration/MaintenanceWindowsTab
 */

import React, { useState, useEffect } from 'react';
import { MaintenanceWindow } from '@shared/types/sla';
import slaService from '@services/slaService';
import MaintenanceWindowForm from '@components/SLA/MaintenanceWindowForm';
import WindowList from '@components/SLA/WindowList';

interface MaintenanceWindowsTabProps {
  /** Optional callback when window is created/updated/deleted */
  onWindowChanged?: () => void;
}

/**
 * Maintenance Windows Tab - Manage scheduled maintenance windows
 */
const MaintenanceWindowsTab: React.FC<MaintenanceWindowsTabProps> = ({ onWindowChanged }) => {
  const [windows, setWindows] = useState<MaintenanceWindow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showWindowForm, setShowWindowForm] = useState(false);
  const [editingWindow, setEditingWindow] = useState<MaintenanceWindow | null>(null);

  useEffect(() => {
    loadWindows();
  }, []);

  const loadWindows = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await slaService.getMaintenanceWindows();
      setWindows(data);
    } catch (err) {
      console.error('Failed to load maintenance windows:', err);
      setError(err instanceof Error ? err.message : 'Failed to load maintenance windows');
    } finally {
      setLoading(false);
    }
  };

  const handleWindowChanged = () => {
    loadWindows();
    setShowWindowForm(false);
    setEditingWindow(null);
    if (onWindowChanged) {
      onWindowChanged();
    }
  };

  const handleEdit = (window: MaintenanceWindow) => {
    setEditingWindow(window);
    setShowWindowForm(true);
  };

  const handleDelete = async (windowId: string) => {
    if (!confirm('Are you sure you want to delete this maintenance window?')) {
      return;
    }

    try {
      await slaService.deleteMaintenanceWindow(windowId);
      handleWindowChanged();
    } catch (err) {
      console.error('Failed to delete maintenance window:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete maintenance window');
    }
  };

  const handleCloseForm = () => {
    setShowWindowForm(false);
    setEditingWindow(null);
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-gray-500'>Loading maintenance windows...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='rounded-md bg-red-50 p-4'>
        <div className='flex'>
          <div className='ml-3'>
            <h3 className='text-sm font-medium text-red-800'>Error Loading Maintenance Windows</h3>
            <div className='mt-2 text-sm text-red-700'>{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h3 className='text-lg font-medium text-gray-900'>Maintenance Windows</h3>
          <p className='mt-1 text-sm text-gray-500'>
            Schedule weekly recurring or one-time maintenance windows. Downtime during maintenance
            windows is excluded from SLA calculations.
          </p>
        </div>
        <button
          type='button'
          onClick={() => setShowWindowForm(true)}
          className='inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500'
        >
          New Window
        </button>
      </div>

      {/* Windows List */}
      <WindowList windows={windows} onEdit={handleEdit} onDelete={handleDelete} />

      {/* Window Form Modal */}
      {showWindowForm && (
        <MaintenanceWindowForm
          window={editingWindow || undefined}
          onWindowChanged={handleWindowChanged}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default MaintenanceWindowsTab;
